/* eslint-disable no-console */
import { oidcService, tokenService, userService } from '@bedrock/fido-sdk';
import { shouldSkipSSI } from '../../../../modules/pairing/pairing.utils';
import { getDeviceBrand } from '../../../app/app.selectors';
import { formatDeviceTitle } from '../../pairing.device.utils';
import { singleSignIn } from '../../platform/default';
import { PAIRING_STATUS_PAIRED, PAIRING_STATUS_PENDING } from '../../pairing.constants';
import { ERROR_AUTHORIZATION_PENDING, ERROR_SLOW_DOWN, GRANT_TYPE } from './pairingProvider.constants';

export * from '../../platform/default';

export const fetchOneTimePasscode = () => async (_, getState) => {
  const params = {};
  try {
    const deviceBrand = getDeviceBrand(getState());
    if (deviceBrand) {
      params.boxName = formatDeviceTitle(deviceBrand);
    }
  } catch (error) {
    throw new Error(`Failed deviceBrand getting: ${error.message}`);
  }

  const { user_code: token, verification_uri_complete: verificationURI } = await oidcService.authorizeDevice();

  // eslint-disable-next-line no-console
  console.log(`[PairingProvider.actions] URL: ${verificationURI}`);

  return { token, verificationURI };
};

export const getProviderPairingStatus = () => async () => {
  const fetchTokensParams = {
    device_code: oidcService.getDeviceCode(),
    grant_type: GRANT_TYPE,
  };

  try {
    const tokens = await oidcService.fetchConnectedTokens(fetchTokensParams);

    if (tokens) {
      return { status: PAIRING_STATUS_PAIRED };
    }
  } catch (error) {
    if (typeof error.message === 'string') {
      const reason = JSON.parse(error.message.replace(/error\s*:\s*/i, ''));

      if (reason.error === ERROR_AUTHORIZATION_PENDING) {
        return { status: PAIRING_STATUS_PENDING };
      }

      if (reason.error === ERROR_SLOW_DOWN) {
        return { status: PAIRING_STATUS_PENDING, slowDown: true };
      }
    }

    throw error;
  }

  return { status: PAIRING_STATUS_PENDING };
};

export const fetchUser =
  (skipRefresh = false) =>
  async () => {
    let shouldTrySSI = false;
    if (!skipRefresh) {
      try {
        await oidcService.renewAccessToken();
      } catch (error) {
        shouldTrySSI = true;
      }
    }

    // With the recent FIDO update, it issues a guest token upon renewing, so the error will never be triggered, hence the manual check
    shouldTrySSI = shouldTrySSI || oidcService.isGuestUser();
    // In case user explicitly logged out manually pressing the logout button, the SSI should be skipped
    if (shouldTrySSI && !shouldSkipSSI()) {
      await singleSignIn();
    }

    const userInfo = await oidcService.getUserInfo();

    if (userInfo && userInfo?.uid) {
      userInfo.firstname = userInfo?.preferred_username || '';
      userInfo.lastname = '';
    }

    return userInfo;
  };

export const removePairing = () => async () => {
  try {
    console.log('[OIDC] Revoking current device');
    await userService.revokeCurrentDevice();
  } catch (error) {
    // NOTE: there may be an error if we have invalid BAT
    console.error(`[OIDC] Can not revoke current device: ${error ? error.message : 'no data'}`);
  }

  try {
    console.log('[OIDC] Logging out from the token provider');
    await oidcService.logout();
  } catch (error) {
    // NOTE: there may be an error in case of concurrent un-pairing, or if we have incorrect OIDC token
    console.error(`[OIDC] Can not log out: ${error ? error.message : 'no data'}`);
  }

  console.log('[OIDC] Clearing local provider tokens');
  oidcService.clearTokens();

  console.log('[OIDC] Clearing BAT');
  tokenService.resetUserAndToken();
};
